/**
 * useRegistryMenu — Fase 3 A7
 *
 * Genera le voci di menu Ant Design a partire da gateRegistry.ts,
 * incrociando i permessi dell'utente corrente letti dall'AuthContext.
 *
 * Regole di inclusione:
 *   - esponi: true, menu: true, layout: 'selector'
 *   - motivoNoMenu falsy (escluse le voci in sviluppo)
 *   - permission: null con senzaGateVoluto → sempre incluse
 *   - permission: string   → OR singolo
 *   - permission: string[] → OR tra i permessi dell'array
 *   - raggruppa le entry per campo `gruppo` (children di una voce padre)
 *   - gruppi passati in excludeGruppi → entry saltate nel sidebar
 *     (usato per 'menu utente': profile/settings/integrazioni restano
 *      nel dropdown utente, ma compaiono in flatVisible per il bottom nav)
 *   - ordine delle voci = ordine del registro
 *
 * Esporta anche `resolveIcon` per convertire il nome-stringa dell'icona
 * nel ReactElement corrispondente (usato dal layout per il bottom nav).
 */

import React, { useContext, useMemo } from 'react'
import type { MenuProps } from 'antd'
import {
  DashboardOutlined,
  FormOutlined,
  TeamOutlined,
  CarOutlined,
  BankOutlined,
  ShopOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  ToolOutlined,
  CalendarOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  AuditOutlined,
  BarChartOutlined,
  DollarOutlined,
  SettingOutlined,
  ApiOutlined,
  MessageOutlined,
  BellOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { AuthContext } from '@/contexts/AuthContext'
import { gateRegistry, type GateEntry } from '@/config/gateRegistry'

// ─── Mappa nome-stringa → ReactElement ────────────────────────────────────────
// Definita a livello di modulo: referenze stabili, nessuna re-creazione per render.

const ICON_MAP: Record<string, React.ReactElement> = {
  DashboardOutlined:   React.createElement(DashboardOutlined),
  FormOutlined:        React.createElement(FormOutlined),
  TeamOutlined:        React.createElement(TeamOutlined),
  CarOutlined:         React.createElement(CarOutlined),
  BankOutlined:        React.createElement(BankOutlined),
  ShopOutlined:        React.createElement(ShopOutlined),
  FileTextOutlined:    React.createElement(FileTextOutlined),
  FileSearchOutlined:  React.createElement(FileSearchOutlined),
  ToolOutlined:        React.createElement(ToolOutlined),
  CalendarOutlined:    React.createElement(CalendarOutlined),
  InboxOutlined:       React.createElement(InboxOutlined),
  CheckCircleOutlined: React.createElement(CheckCircleOutlined),
  AuditOutlined:       React.createElement(AuditOutlined),
  BarChartOutlined:    React.createElement(BarChartOutlined),
  DollarOutlined:      React.createElement(DollarOutlined),
  SettingOutlined:     React.createElement(SettingOutlined),
  ApiOutlined:         React.createElement(ApiOutlined),
  MessageOutlined:     React.createElement(MessageOutlined),
  BellOutlined:        React.createElement(BellOutlined),
  UserOutlined:        React.createElement(UserOutlined),
}

/**
 * Converte il nome-stringa di un'icona (dal registro) nel ReactElement.
 * Restituisce undefined per null o nomi non presenti nella mappa.
 */
export function resolveIcon(name: string | null): React.ReactElement | undefined {
  if (!name) return undefined
  return ICON_MAP[name]
}

// ─── Tipi ─────────────────────────────────────────────────────────────────────

interface UseRegistryMenuOptions {
  /**
   * Gruppi da escludere dalla struttura ad albero del sidebar.
   * Le entry rimangono in `flatVisible` per il bottom nav e altri usi.
   * Esempio: ['menu utente'] esclude profile/settings/integrazioni dalla sidebar
   * (gestiti dal dropdown utente), ma profile resta nel bottom nav via flatVisible.
   */
  excludeGruppi?: string[]
}

export interface UseRegistryMenuResult {
  /** Voci per il componente Menu Ant Design (prima livello + sotto-menu per gruppo) */
  items: MenuProps['items']
  /**
   * Tutte le entry visibili all'utente (flat, prima del raggruppamento).
   * Usata per il bottom nav e per qualsiasi lookup per path.
   */
  flatVisible: GateEntry[]
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRegistryMenu(
  options: UseRegistryMenuOptions = {},
): UseRegistryMenuResult {
  const { excludeGruppi = [] } = options

  const authContext = useContext(AuthContext)
  const user    = authContext?.user        ?? null
  const perms   = authContext?.permissions ?? []

  // Stringa stabile per la dipendenza del memo: evita re-run se il chiamante
  // passa un array letterale nuovo a ogni render con lo stesso contenuto.
  const excludeKey = excludeGruppi.join('\x00')

  return useMemo(() => {
    const excluded = new Set(excludeGruppi)

    // ── Controllo permesso (senza chiamare hook in un loop) ────────────────
    const hasPermission = (perm: string | string[] | null): boolean => {
      if (perm === null) return true          // senzaGateVoluto → sempre visibile
      if (!user) return false
      const list = Array.isArray(perm) ? perm : [perm]
      return list.some(p => perms.includes(p))
    }

    // ── 1. Candidati dal registro ──────────────────────────────────────────
    const candidates = gateRegistry.filter(
      e =>
        e.esponi &&
        e.menu &&
        e.layout === 'selector' &&
        !e.motivoNoMenu,   // esclude funzioni "in sviluppo" e voci disabilitate
    )

    // ── 2. Filtra per permesso utente ─────────────────────────────────────
    const visible = candidates.filter(e => hasPermission(e.permission))
    const visibleSet = new Set(visible)

    // ── 3. Pre-computa children per gruppo (solo voci visibili) ───────────
    const groupChildren = new Map<string, GateEntry[]>()
    for (const e of visible) {
      if (e.gruppo && !excluded.has(e.gruppo)) {
        if (!groupChildren.has(e.gruppo)) groupChildren.set(e.gruppo, [])
        groupChildren.get(e.gruppo)!.push(e)
      }
    }

    // ── 4. Costruisci items percorrendo il registro (preserva l'ordine) ───
    const emittedGroups = new Set<string>()
    const items: MenuProps['items'] = []

    for (const entry of gateRegistry) {
      if (!visibleSet.has(entry)) continue

      const g = entry.gruppo

      if (g === null) {
        // Voce di primo livello senza gruppo
        items.push({
          key:   entry.path,
          icon:  resolveIcon(entry.icon),
          label: entry.label,
        })
      } else if (excluded.has(g)) {
        // Gruppo escluso dalla sidebar (es. 'menu utente') → voce saltata
        // (rimane in flatVisible per bottom nav)
      } else {
        // Voce che appartiene a un gruppo → emetti il SubMenu una sola volta
        if (!emittedGroups.has(g)) {
          emittedGroups.add(g)
          const children = groupChildren.get(g) ?? []
          items.push({
            key:      `gruppo-${g}`,
            icon:     resolveIcon(children[0]?.icon ?? null),
            label:    g,
            children: children.map(c => ({
              key:   c.path,
              icon:  resolveIcon(c.icon),
              label: c.label,
            })),
          })
        }
      }
    }

    return { items, flatVisible: visible }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, perms, excludeKey])
}
